'use strict';

/**
 * GLOBAL HOLOGRAM - Unified Starfield System
 * Features:
 * - Parallax stars tracking mouse
 * - Dynamic nebula color shifts per section
 * - Automatic visibility (Hidden on Home & Menu)
 * - Optimized performance (Pause when hidden)
 */

const hologramDebounce = (fn, delay) => {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

class GlobalHologram {
    constructor(containerId) {
        this.container = document.getElementById(containerId);
        if (!this.container) return;

        this.mouse = { x: 0.5, y: 0.5 };
        this.targetMouse = { x: 0.5, y: 0.5 };
        this.isActive = true;
        this.time = 0;
        this.currentPalette = 'default';

        this.init();
    }

    init() {
        this.createCanvas();
        this.createStars();
        this.createNebula();
        this.bindEvents();
        this.animate();
    }

    createCanvas() {
        this.canvas = document.createElement('canvas');
        this.canvas.className = 'hologram-canvas';
        this.ctx = this.canvas.getContext('2d');
        this.container.appendChild(this.canvas);
        this.resizeCanvas();
    }

    resizeCanvas() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
        this.createStars();
        this.createNebula();
    }

    createStars() {
        this.stars = [];
        const count = Math.floor((this.canvas.width * this.canvas.height) / 2000);

        for (let i = 0; i < count; i++) {
            this.stars.push({
                x: Math.random() * this.canvas.width,
                y: Math.random() * this.canvas.height,
                baseX: Math.random() * this.canvas.width,
                baseY: Math.random() * this.canvas.height,
                size: Math.random() * 2.2 + 0.3,
                alpha: Math.random() * 0.7 + 0.2,
                phase: Math.random() * Math.PI * 2,
                speed: 0.008 + Math.random() * 0.02,
                depth: Math.random() * 0.6 + 0.4
            });
        }
    }

    createNebula() {
        const palettes = {
            'default': [
                { x: 0.2, y: 0.3, radius: 400, color: 'rgba(45, 104, 196, 0.05)' },
                { x: 0.8, y: 0.6, radius: 450, color: 'rgba(136, 71, 199, 0.04)' },
                { x: 0.5, y: 0.8, radius: 350, color: 'rgba(0, 212, 255, 0.03)' }
            ],
            'contact': [
                { x: 0.3, y: 0.4, radius: 500, color: 'rgba(255, 60, 0, 0.06)' },
                { x: 0.7, y: 0.3, radius: 400, color: 'rgba(136, 71, 199, 0.05)' },
                { x: 0.5, y: 0.7, radius: 450, color: 'rgba(45, 104, 196, 0.04)' }
            ]
        };
        this.nebulae = palettes[this.currentPalette] || palettes.default;
    }

    bindEvents() {
        window.addEventListener('mousemove', (e) => {
            this.targetMouse.x = e.clientX / window.innerWidth;
            this.targetMouse.y = e.clientY / window.innerHeight;
        });

        window.addEventListener('resize', () => this.resizeCanvas());

        // Master Visibility & State Update
        const sections = document.querySelectorAll('.l-section');
        const perspective = document.querySelector('.perspective');

        const updateState = () => {
            const activeSection = Array.from(sections).findIndex(s => s.classList.contains('section--is-active')) + 1;
            const isMenuOpen = perspective?.classList.contains('perspective--modalview') || perspective?.classList.contains('modalview');
            const isHome = activeSection === 1;

            // Visibility Rules - Allow stars during menu/perspective mode
            const shouldBeVisible = !isHome && !document.hidden;

            if (shouldBeVisible) {
                if (!this.isActive) {
                    this.container.style.opacity = '1';
                    this.container.style.visibility = 'visible';
                    this.isActive = true;
                    this.animate();
                }

                // Dynamic Palette Switch
                const newPalette = (activeSection === 4) ? 'contact' : 'default';
                if (newPalette !== this.currentPalette) {
                    this.currentPalette = newPalette;
                    this.createNebula();
                }
            } else {
                this.isActive = false;
                this.container.style.opacity = '0';
                this.container.style.visibility = 'hidden';
            }
        };

        // Watch for section changes (MutationObserver on parent or each section)
        const updateStateDebounced = hologramDebounce(() => updateState(), 50);
        const sectionObserver = new MutationObserver(updateStateDebounced);
        sections.forEach(s => sectionObserver.observe(s, { attributes: true, attributeFilter: ['class'] }));

        // Watch for menu toggle (MutationObserver on perspective)
        if (perspective) {
            const menuObserver = new MutationObserver(updateStateDebounced);
            menuObserver.observe(perspective, { attributes: true, attributeFilter: ['class'] });
        }

        document.addEventListener('visibilitychange', () => updateState());
        this.updateState = updateState;
        updateState();
    }

    animate() {
        if (!this.isActive) return;

        this.time += 0.012;
        this.mouse.x += (this.targetMouse.x - this.mouse.x) * 0.25;
        this.mouse.y += (this.targetMouse.y - this.mouse.y) * 0.25;

        this.draw();
        requestAnimationFrame(() => this.animate());
    }

    draw() {
        const { ctx, canvas } = this;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        this.drawNebula();
        this.drawStars();
        this.drawScanLines();
    }

    drawNebula() {
        const { ctx, canvas } = this;
        const w = canvas.width;
        const h = canvas.height;
        const timeFactor = Math.sin(this.time * 0.4) * 0.08;

        this.nebulae.forEach(nebula => {
            const x = nebula.x * w;
            const y = nebula.y * h;
            const radius = nebula.radius * (1 + timeFactor);

            const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
            gradient.addColorStop(0, nebula.color);
            gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
        });
    }

    drawStars() {
        const { ctx, canvas, mouse } = this;
        const w = canvas.width;
        const h = canvas.height;
        const offsetX = (mouse.x - 0.5) * 40;
        const offsetY = (mouse.y - 0.5) * 40;
        const isContact = this.currentPalette === 'contact';
        const glowBase = isContact ? 'rgba(255, 100, 50,' : 'rgba(200, 220, 255,';

        this.stars.forEach(star => {
            star.phase += star.speed;
            const twinkle = (Math.sin(star.phase) + 1) * 0.5;
            const alpha = star.alpha * (0.3 + twinkle * 0.7);

            const px = star.baseX + offsetX * star.depth;
            const py = star.baseY + offsetY * star.depth;

            // Efficient screen wrapping
            star.x = ((px % w) + w) % w;
            star.y = ((py % h) + h) % h;

            const x = star.x;
            const y = star.y;
            const size = star.size;

            ctx.beginPath();
            ctx.arc(x, y, size, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.fill();

            if (size > 1.4) {
                const glowSize = size * 3.5;
                const glow = ctx.createRadialGradient(x, y, 0, x, y, glowSize);
                glow.addColorStop(0, `${glowBase}${alpha * 0.35})`);
                glow.addColorStop(1, `${glowBase}0)`);
                ctx.beginPath();
                ctx.arc(x, y, glowSize, 0, Math.PI * 2);
                ctx.fillStyle = glow;
                ctx.fill();
            }
        });
    }

    drawScanLines() {
        const { ctx, canvas } = this;
        const w = canvas.width;
        const h = canvas.height;

        // Static scan lines optimized with pattern
        if (!this.scanPattern) {
            const pCanvas = document.createElement('canvas');
            pCanvas.width = 1;
            pCanvas.height = 4;
            const pCtx = pCanvas.getContext('2d');
            pCtx.fillStyle = 'rgba(0, 0, 0, 0.02)';
            pCtx.fillRect(0, 0, 1, 2);
            this.scanPattern = ctx.createPattern(pCanvas, 'repeat');
        }

        ctx.fillStyle = this.scanPattern;
        ctx.fillRect(0, 0, w, h);

        const scanY = (this.time * 80) % h;
        const isContact = (this.currentPalette === 'contact');
        const scanColor = isContact ? 'rgba(255, 60, 0,' : 'rgba(0, 212, 255,';

        const gradient = ctx.createLinearGradient(0, scanY - 25, 0, scanY + 25);
        gradient.addColorStop(0, `${scanColor}0)`);
        gradient.addColorStop(0.5, `${scanColor}0.04)`);
        gradient.addColorStop(1, `${scanColor}0)`);

        ctx.fillStyle = gradient;
        ctx.fillRect(0, scanY - 25, w, 50);
    }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        new GlobalHologram('global-parallax-container');
    }, 150);
});
